import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  ListCustomViewsInput,
  CustomView,
  ListCustomViewsResponse,
} from '@erp/rpc';

/**
 * Hook for fetching custom views for a specific module and section
 */
export function useCustomViews(input: ListCustomViewsInput) {
  return useQuery(orpc.core.customViews.list.queryOptions({ input }));
}

/**
 * Hook for creating a new custom view
 */
export function useCreateCustomView() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.core.customViews.create.mutationOptions({
      onSuccess: (newView, variables) => {
        // Update cache directly with the new view instead of refetching
        const queryKey = orpc.core.customViews.list.queryKey({
          input: {
            module: variables.module,
            section: variables.section,
          },
        });

        queryClient.setQueryData<ListCustomViewsResponse>(
          queryKey,
          (oldData) => {
            if (!oldData) return { views: [newView] };
            return {
              ...oldData,
              views: [newView, ...oldData.views],
            };
          },
        );
      },
    }),
  );
}

/**
 * Hook for updating a custom view
 */
export function useUpdateCustomView() {
  const queryClient = useQueryClient();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.core.customViews.list.key(),
  ]);

  return useMutation(
    orpc.core.customViews.update.mutationOptions({
      onSuccess: (updatedView) => {
        if (updatedView && updatedView.module && updatedView.section) {
          // Update cache directly with the updated view instead of refetching
          const queryKey = orpc.core.customViews.list.queryKey({
            input: {
              module: updatedView.module,
              section: updatedView.section,
            },
          });

          queryClient.setQueryData<ListCustomViewsResponse>(
            queryKey,
            (oldData) => {
              if (!oldData?.views) return oldData;
              return {
                ...oldData,
                views: oldData.views.map((view) =>
                  view.id === updatedView.id ? updatedView : view,
                ),
              };
            },
          );
        } else {
          // Fallback: invalidate all custom views queries if data is missing
          invalidateRpcQueryFn();
        }
      },
    }),
  );
}

/**
 * Hook for deleting a custom view
 */
export function useDeleteCustomView() {
  const queryClient = useQueryClient();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.core.customViews.list.key(),
  ]);

  return useMutation(
    orpc.core.customViews.delete.mutationOptions({
      onSuccess: (data, variables) => {
        if (data.success && data.module && data.section) {
          // Update cache directly by removing the deleted view instead of refetching
          const queryKey = orpc.core.customViews.list.queryKey({
            input: {
              module: data.module,
              section: data.section,
            },
          });

          queryClient.setQueryData<ListCustomViewsResponse>(
            queryKey,
            (oldData) => {
              if (!oldData?.views) return oldData;
              return {
                ...oldData,
                views: oldData.views.filter((view) => view.id !== variables.id),
              };
            },
          );
        } else {
          // Fallback: invalidate all custom views queries
          invalidateRpcQueryFn();
        }
      },
    }),
  );
}

/**
 * Hook for reordering custom views
 */
export function useReorderCustomViews() {
  const queryClient = useQueryClient();

  return useMutation(
    orpc.core.customViews.reorder.mutationOptions({
      onSuccess: (_, variables) => {
        // Update cache directly with the new order instead of refetching
        const queryKey = orpc.core.customViews.list.queryKey({
          input: {
            module: variables.module,
            section: variables.section,
          },
        });

        queryClient.setQueryData<ListCustomViewsResponse>(
          queryKey,
          (oldData) => {
            if (!oldData?.views) return oldData;

            // Create a map for quick lookup
            const viewsMap = new Map<string, CustomView>(
              oldData.views.map((view) => [view.id, view]),
            );

            // Reorder according to orderedIds (preserving the order from the input)
            const reorderedViews = variables.orderedIds
              .map((id) => viewsMap.get(id))
              .filter((view): view is CustomView => view !== undefined);

            return {
              ...oldData,
              views: reorderedViews,
            };
          },
        );
      },
    }),
  );
}
